import utmVocabulary from "./utmVocabulary.json";

export const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
];
export const KIT_UTM_FIELD_KEYS = [...UTM_KEYS, "first_touch_url"];
export const FIRST_TOUCH_KEY = "ie_first_touch";
export const SESSION_KEY = "ie_track_sid";
export const TRACK_EXCLUDE_KEY = "ie_track_exclude";
export const LAST_LANDING_KEY = "ie_track_last_landing";
export const AUTH_COOKIE_NAME = "gi-interview-questions-Tool-user";
export const MAX_UTM_LENGTH = 100;
const STRIP_QUERY_KEYS = [...UTM_KEYS, "ie_track"];

export { utmVocabulary };

export const cleanUtmValue = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .slice(0, MAX_UTM_LENGTH);

/** Like cleanUtmValue, but spaces become underscores so a typed name can be a slug. */
export const slugUtmValue = (value) =>
  cleanUtmValue(
    String(value || "")
      .trim()
      .replace(/\s+/g, "_"),
  ).replace(/^_+|_+$/g, "");

export const cleanPath = (value) => {
  let path = String(value || "/");
  try {
    if (/^https?:\/\//i.test(path)) {
      path = new URL(path).pathname;
    }
  } catch {
    path = "/";
  }
  path = path.split("?")[0].split("#")[0];
  if (!path.startsWith("/")) path = `/${path}`;
  return path.slice(0, 200) || "/";
};

export const readUtmsFromSearch = (search) => {
  const params = new URLSearchParams(
    typeof search === "string" ? search : "",
  );
  const data = {};
  UTM_KEYS.forEach((key) => {
    const raw = params.get(key);
    if (raw) data[key] = cleanUtmValue(raw);
  });
  return data;
};

export const hasUtmValues = (data) =>
  UTM_KEYS.some((key) => Boolean(data && data[key]));

const readJson = (store, key) => {
  try {
    const raw = store.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
};

export const getFirstTouch = () => {
  if (typeof window === "undefined") return {};
  return readJson(window.localStorage, FIRST_TOUCH_KEY) || {};
};

export const getSessionId = () => {
  if (typeof window === "undefined") return "";
  try {
    const existing = window.sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id =
      (typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(16).slice(2)}`);
    window.sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return `s-${Date.now()}`;
  }
};

export const captureFirstTouch = (search, pathname) => {
  if (typeof window === "undefined") return getFirstTouch();
  try {
    if (window.localStorage.getItem(FIRST_TOUCH_KEY)) {
      return getFirstTouch();
    }
    const query =
      search == null ? window.location.search : search;
    const data = readUtmsFromSearch(query);
    if (!hasUtmValues(data)) return {};
    const path =
      pathname == null ? window.location.pathname : pathname;
    const payload = {
      ...data,
      first_touch_url: cleanPath(path),
    };
    window.localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(payload));
    return payload;
  } catch {
    return {};
  }
};

/** Drop utm_* and ie_track from the address bar. Keeps connected, preview, joininvite, hash. */
export const stripUtmParamsFromLocation = () => {
  if (typeof window === "undefined" || !window.history?.replaceState) {
    return "";
  }
  try {
    const url = new URL(window.location.href);
    let changed = false;
    STRIP_QUERY_KEYS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });
    if (!changed) return url.search;
    const search = url.searchParams.toString();
    const next = `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
    window.history.replaceState(window.history.state, "", next);
    return search ? `?${search}` : "";
  } catch {
    return "";
  }
};

export const isManagePath = (pathname) => {
  const path = String(pathname || "/")
    .replace(/\/+$/, "")
    .toLowerCase() || "/";
  return path === "/manage" || path.startsWith("/manage/");
};

export const isDevHost = (hostname) => {
  const host = String(hostname || "").toLowerCase();
  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host === "[::1]" ||
    host === "::1" ||
    host.endsWith(".local")
  );
};

const readCookie = (name) => {
  if (typeof document === "undefined" || !document.cookie) return "";
  const prefix = `${name}=`;
  const hit = document.cookie.split("; ").find((row) => row.startsWith(prefix));
  if (!hit) return "";
  return decodeURIComponent(hit.slice(prefix.length));
};

export const decodeJwtPayload = (token) => {
  try {
    let raw = String(token || "").trim();
    if (!raw) return null;
    if (raw.startsWith("{") || raw.startsWith('"')) {
      const parsed = JSON.parse(raw);
      raw = typeof parsed === "string" ? parsed : parsed?.token || "";
    }
    const part = raw.split(".")[1];
    if (!part) return null;
    const padded = part.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(padded);
    return JSON.parse(json);
  } catch {
    return null;
  }
};

export const isAdminSession = () => {
  if (typeof window === "undefined") return false;
  const payload = decodeJwtPayload(readCookie(AUTH_COOKIE_NAME));
  return Boolean(payload && payload.isAdmin === true);
};

export const isThisBrowserExcluded = () => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(TRACK_EXCLUDE_KEY) === "1";
  } catch {
    return false;
  }
};

export const setThisBrowserExcluded = (excluded) => {
  if (typeof window === "undefined") return false;
  try {
    if (excluded) window.localStorage.setItem(TRACK_EXCLUDE_KEY, "1");
    else window.localStorage.removeItem(TRACK_EXCLUDE_KEY);
    return isThisBrowserExcluded();
  } catch {
    return false;
  }
};

const rememberLastLanding = (sessionId) => {
  if (typeof window === "undefined") return;
  const id = String(sessionId || "").trim();
  if (!id) return;
  try {
    window.localStorage.setItem(
      LAST_LANDING_KEY,
      JSON.stringify({ sessionId: id, at: Date.now() }),
    );
  } catch {
    /* ignore */
  }
};

/** Session id of the last landing this browser actually sent, if any. */
export const getLastLandingSessionId = () => {
  if (typeof window === "undefined") return "";
  const stored = readJson(window.localStorage, LAST_LANDING_KEY);
  if (stored && stored.sessionId) return String(stored.sessionId).trim();
  return "";
};

export const clearLastLanding = () => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(LAST_LANDING_KEY);
  } catch {
    /* ignore */
  }
};

export const applyTrackOptQuery = (search) => {
  if (typeof window === "undefined") return null;
  const params = new URLSearchParams(
    search == null ? window.location.search : search,
  );
  const raw = String(params.get("ie_track") || "").toLowerCase();
  if (raw === "0" || raw === "off" || raw === "false") {
    setThisBrowserExcluded(true);
    return "exclude";
  }
  if (raw === "1" || raw === "on" || raw === "true") {
    setThisBrowserExcluded(false);
    return "include";
  }
  return null;
};

export const shouldSkipTracking = ({
  search,
  pathname,
  hostname,
} = {}) => {
  if (typeof window === "undefined") return true;
  const query = search == null ? window.location.search : search;
  const path = pathname == null ? window.location.pathname : pathname;
  const host = hostname == null ? window.location.hostname : hostname;
  const opt = applyTrackOptQuery(query);
  if (isManagePath(path)) return true;
  if (opt === "include") return false;
  if (opt === "exclude") return true;
  try {
    if (window.localStorage.getItem(TRACK_EXCLUDE_KEY) === "1") return true;
  } catch {
    /* ignore */
  }
  if (process.env.NODE_ENV === "development" || isDevHost(host)) {
    if (process.env.NODE_ENV !== "test") return true;
  }
  if (isAdminSession()) return true;
  return false;
};

export const buildUtmUrl = ({
  source,
  medium,
  campaign,
  content,
  site,
} = {}) => {
  const base = String(site || utmVocabulary.site || "https://ignite-elysium.com")
    .replace(/\/$/, "");
  const src = cleanUtmValue(source);
  if (!src) return `${base}/`;
  if (src === "gab") return `${base}/?utm_source=gab`;
  const params = new URLSearchParams();
  params.set("utm_source", src);
  const med = cleanUtmValue(medium);
  if (med) params.set("utm_medium", med);
  params.set(
    "utm_campaign",
    cleanUtmValue(campaign || utmVocabulary.defaultCampaign),
  );
  const place = cleanUtmValue(content);
  if (place) params.set("utm_content", place);
  return `${base}/?${params.toString()}`;
};

export const getApiBase = () => {
  const fromEnv = process.env.REACT_APP_API_URL;
  if (fromEnv && String(fromEnv).trim()) {
    return String(fromEnv).trim().replace(/\/$/, "");
  }
  const inDevelopment = process.env.NODE_ENV === "development";
  if (inDevelopment && typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host && host !== "localhost" && host !== "127.0.0.1") {
      return `http://${host}:8000`;
    }
    return "http://localhost:8000";
  }
  if (inDevelopment) return "http://localhost:8000";
  return "https://api.ignite-elysium.com";
};

export const getTrackEventUrl = () => `${getApiBase()}/api/track/event`;

const attributionFrom = (data) => ({
  source: cleanUtmValue(data.utm_source) || "direct",
  medium: cleanUtmValue(data.utm_medium),
  campaign: cleanUtmValue(data.utm_campaign),
  content: cleanUtmValue(data.utm_content),
});

export const currentVisitAttribution = (search) => {
  const query =
    search == null && typeof window !== "undefined"
      ? window.location.search
      : search;
  const fromUrl = readUtmsFromSearch(query);
  if (hasUtmValues(fromUrl)) return attributionFrom(fromUrl);
  return { source: "direct", medium: "", campaign: "", content: "" };
};

export const signupAttribution = (search) => {
  const first = getFirstTouch();
  if (hasUtmValues(first)) return attributionFrom(first);
  return currentVisitAttribution(search);
};

export const sendTrackEvent = async (payload) => {
  if (typeof fetch !== "function") return;
  if (shouldSkipTracking()) return;
  try {
    const body = {
      event: payload.event,
      source: cleanUtmValue(payload.source) || "direct",
      medium: cleanUtmValue(payload.medium),
      campaign: cleanUtmValue(payload.campaign),
      content: cleanUtmValue(payload.content),
      path: cleanPath(
        payload.path ||
          (typeof window !== "undefined" ? window.location.pathname : "/"),
      ),
      sessionId: payload.sessionId || getSessionId(),
    };
    if (body.event === "landing") rememberLastLanding(body.sessionId);
    await fetch(getTrackEventUrl(), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify(body),
      keepalive: true,
      credentials: "omit",
    });
  } catch {
    /* tracking must never break the app */
  }
};

export const recordLanding = (search, pathname) => {
  const attrib = currentVisitAttribution(search);
  return sendTrackEvent({
    event: "landing",
    ...attrib,
    path:
      pathname == null && typeof window !== "undefined"
        ? window.location.pathname
        : pathname,
    sessionId: getSessionId(),
  });
};

export const recordSignup = (search, pathname) => {
  const attrib = signupAttribution(search);
  return sendTrackEvent({
    event: "signup",
    ...attrib,
    path:
      pathname == null && typeof window !== "undefined"
        ? window.location.pathname
        : pathname,
    sessionId: getSessionId(),
  });
};

export const appendKitUtmFields = (formData, firstTouch) => {
  const ft = firstTouch && typeof firstTouch === "object" ? firstTouch : {};
  KIT_UTM_FIELD_KEYS.forEach((key) => {
    if (ft[key]) formData.append(`fields[${key}]`, String(ft[key]));
  });
  return formData;
};

export const initUtmTracking = () => {
  if (typeof window === "undefined") return;
  const search = window.location.search;
  const pathname = window.location.pathname;
  const skip = shouldSkipTracking({ search, pathname });
  if (skip) {
    stripUtmParamsFromLocation();
    return;
  }
  getSessionId();
  const hadFirstTouch = hasUtmValues(getFirstTouch());
  captureFirstTouch(search, pathname);
  if (!hadFirstTouch) {
    void recordLanding(search, pathname);
  }
  stripUtmParamsFromLocation();
};
